/**
 * Processing-fee handling for basket totals.
 *
 * The fee is a CHECKOUT-ONLY line: it is itemised and charged on the checkout
 * page, but excluded from the basket preview so the drawer quotes the cost of
 * the items alone. The backend has no payment-method context when it builds the
 * basket for `getBasket`, so it always folds the fee into `total`/`chargeTotal`;
 * stripping it for preview surfaces is therefore the frontend's job.
 *
 * Shared so the collapsed footer bar and the expanded summary cannot drift —
 * they previously disagreed by £1.
 */

type FeeLike = { value?: number | null } | null | undefined;

type BasketLike = {
  total?: number | null;
  chargeTotal?: number | null;
  feeItems?: FeeLike[] | null;
} | null | undefined;

/** Sum of all fee lines on the basket, in pence. */
export const basketFeeTotal = (basket: BasketLike): number => (basket?.feeItems ?? [])
  .reduce((sum: number, fee: FeeLike) => sum + (fee?.value ?? 0), 0);

/** Basket total excluding fees — for preview surfaces only. */
export const previewTotal = (basket: BasketLike): number => Math.max(
  0,
  (basket?.total ?? 0) - basketFeeTotal(basket),
);

/** Amount payable now excluding fees — for preview surfaces only. */
export const previewChargeTotal = (basket: BasketLike): number => Math.max(
  0,
  (basket?.chargeTotal ?? 0) - basketFeeTotal(basket),
);
